import React, { useState, useCallback, useMemo, useRef, useEffect } from 'react'
import { GoogleMap, Marker, useLoadScript } from '@react-google-maps/api'
import GoogleMapZonePolygon from './GoogleMapZonePolygon'
import Loader from './Spinner'
import proj4 from 'proj4'
import { useQuery } from '@apollo/client'
import { GET_ALL_JURISDICTIONS_AND_SUBS } from '../../graphql/Catalog/Jurisdictions/jurisdictions'
import GooglePlacesSearch from '../Admin/Catalogs/ReportWeb/components/Location/helpers/GooglePlacesSearch'
import { toastSweetAlert } from '../Helpers/ToastSweetAlert'
import { extractAddressComponents } from '../Admin/Catalogs/ReportWeb/components/Location/helpers/functions'

const API_KEY = process.env.REACT_APP_API_KEY_GOOGLE_MAPS
const urlGeoJson = '/geomatica/zonas_tlajomulco.geojson'
const urlGeoJsonColonies = '/geomatica/colonias_tlajomulco.geojson'
const libraries = ['places', 'geometry', 'kml']
const utmZone13N = '+proj=utm +zone=13 +datum=WGS84 +units=m +no_defs'

const GoogleMapComponentV3 = React.memo(
  ({
    mapContainerStyle,
    optionsGoogleMap = {},
    stylesGoogleMap = [],
    onChange = () => {},
    disabled = true,
    defaultCenter = {
      lat: 20.473623708878552,
      lng: -103.44747586737101, //TLAJOMULCO DEFAULT
    },
    jurisdictionId = 1,
    showInputPlaces = true,
    addressValue = '',
    dataEdit = false,
  }) => {
    const [mapDataState, setMapDataState] = useState({
      geoZonesCoords: [],
      geoColoniesCoords: [],
    })

    const mapDataRef = useRef(mapDataState)
    const hasInitializedDataEdit = useRef(false)

    const { isLoaded, loadError } = useLoadScript({
      googleMapsApiKey: API_KEY,
      libraries,
    })

    const {
      data: dataSubs,
      loading: loadingSubs,
      error: errorSubs,
    } = useQuery(
      GET_ALL_JURISDICTIONS_AND_SUBS,
      { variables: { jurisdiction_id: jurisdictionId } },
      { fetchPolicy: 'no-cache' }
    )

    const subJurisdictionsReady =
      !loadingSubs && dataSubs?.getAllJurisdictionsAndSubs?.length > 0

    const parsedSubJurisdictions = useMemo(() => {
      if (!subJurisdictionsReady) return []
      const list = []
      dataSubs.getAllJurisdictionsAndSubs.forEach((jurisdiction) => {
        jurisdiction.SubsOfJurisdiction.forEach((sub) =>
          list.push({
            id: sub?.id,
            name: sub?.name,
          })
        )
      })
      return list
    }, [subJurisdictionsReady, dataSubs])

    useEffect(() => {
      mapDataRef.current = mapDataState
    }, [mapDataState])

    const onLoad = useCallback(
      (map) => {
        if (!parsedSubJurisdictions.length) return

        const loadZones = new Promise(async (resolve) => {
          map.data.loadGeoJson(urlGeoJson, null, async (features) => {
            const dataZonesCoords = []
            const bounds = new window.google.maps.LatLngBounds()

            for await (const feature of features) {
              const zoneName = feature.getProperty('nombre')
              const coords = feature
                .getGeometry()
                .getArray()[0]
                .getArray()[0]
                .getArray()
                .map(
                  (item) =>
                    new window.google.maps.LatLng(item.lat(), item.lng())
                )
              const findZone = parsedSubJurisdictions.find(
                (item) => item.name === zoneName
              )

              if (findZone) {
                dataZonesCoords.push({
                  zone: {
                    id: findZone.id,
                    name: zoneName,
                    strokeColor: findZone?.strokeColor ?? '#000000',
                    fillColor: findZone?.fillColor ?? '#ff8819',
                  },
                  coords,
                  centroid: getPolygonCentroid(coords),
                  polygon: new window.google.maps.Polygon({ paths: coords }),
                })

                coords.forEach((coord) => bounds.extend(coord))
              }
            }

            resolve(dataZonesCoords)
          })
        })

        const loadColonies = new Promise(async (resolve) => {
          try {
            const response = await fetch(urlGeoJsonColonies)
            const geojson = await response.json()
            const dataColoniesCoords = []

            for await (const feature of geojson.features) {
              const geoCoords = feature.geometry.coordinates[0][0].map(
                (utm) => {
                  const [lng, lat] = proj4(utmZone13N, 'WGS84', [
                    utm[0],
                    utm[1],
                  ])
                  return { lat, lng }
                }
              )

              dataColoniesCoords.push({
                nocolonia: feature.properties.nocolonia,
                name: feature.properties.nombre,
                coords: geoCoords,
              })
            }

            resolve(dataColoniesCoords)
          } catch (err) {
            console.error('Error al cargar el GeoJSON de colonias:', err)
            resolve({ geoColoniesCoords: [] }) // aún así resolvemos
          }
        })

        // Ejecutar ambas en paralelo y hacer un único setMapDataState
        Promise.all([loadZones, loadColonies]).then(
          ([zonesData, coloniesData]) => {
            const newState = {
              geoZonesCoords: zonesData,
              geoColoniesCoords: coloniesData,
            }
            // markerPosition : position,

            // Solo usar dataEdit si existe y aún no se ha usado
            if (dataEdit.edit && !hasInitializedDataEdit.current) {
              newState.markerPosition = dataEdit.markerPosition ?? null
              newState.full_address = dataEdit.full_address ?? ''
              newState.address_components = dataEdit.address ?? {}
              newState.url = dataEdit.url ?? ''
              newState.zone_id = dataEdit.zone_id ?? null
              newState.zone_name = dataEdit.zone_name ?? ''
              newState.colony_name = dataEdit.colony_name ?? ''
              newState.colony_number = dataEdit.colony_number ?? null
              newState.addressValue = dataEdit.full_address ?? ''

              hasInitializedDataEdit.current = true
              map.panTo(newState.markerPosition)
              map.setZoom(17)
            }

            setMapDataState((prev) => ({
              ...prev,
              ...newState,
            }))
          }
        )
        map.data.setStyle({ strokeWeight: 0.3 })
      },
      [parsedSubJurisdictions, dataEdit]
    )

    const getPolygonCentroid = (coords) => {
      let area = 0
      let lat = 0
      let lng = 0

      for (let i = 0; i < coords.length; i++) {
        const j = (i + 1) % coords.length
        const xi = coords[i].lat()
        const yi = coords[i].lng()
        const xj = coords[j].lat()
        const yj = coords[j].lng()
        const a = xi * yj - xj * yi
        area += a
        lat += (xi + xj) * a
        lng += (yi + yj) * a
      }

      area /= 2
      lat /= 6 * area
      lng /= 6 * area

      return { lat, lng }
    }

    const handleClickMap = useCallback(async (event) => {
      if (!window.google)
        return handleAlertError('Google Maps API no está cargado') // protección extra

      const address_places = document.getElementById('address_places')
      address_places.classList.remove('is-invalid')

      const lat = event.latLng.lat()
      const lng = event.latLng.lng()
      const markerPosition = { lat, lng }

      const result = await getLocationFromLatLng(lat, lng)
      // simula el objeto esperado sin usar geocoder
      const url = `https://www.google.com/maps?q=${lat},${lng}`

      const geocoder = new window.google.maps.Geocoder()

      geocoder.geocode({ location: markerPosition }, (results, status) => {
        if (status !== 'OK' || !results[0]) {
          handleAlertError(
            'No se pudo obtener la dirección de la ubicación seleccionada'
          )
          return
        }

        const foundAddress = results[0].formatted_address
        const foundAddressComponents = results[0].address_components
        const extractAddress = extractAddressComponents(foundAddressComponents)

        setMapDataState((prev) => ({
          ...prev,
          markerPosition: markerPosition,
          full_address: foundAddress,
          address_components: extractAddress,
          addressValue: foundAddress,
          ...result,
        }))

        onChange({
          markerPosition,
          full_address: foundAddress,
          address_components: extractAddress,
          ...result,
          url,
          disabled,
        })
      })
    }, [])

    const handleClickPlace = async (place) => {
      if (!place || !place.geometry) {
        handleAlertError('No se encontró ubicación')
        return
      }

      const lat = place.geometry.location.lat()
      const lng = place.geometry.location.lng()
      const markerPosition = { lat, lng }

      const result = await getLocationFromLatLng(lat, lng)

      if (result?.error) return handleAlertError(result.message)

      // simula el objeto esperado sin usar geocoder
      const url = `https://www.google.com/maps?q=${lat},${lng}`
      const extractAddress = extractAddressComponents(place.address_components)

      setMapDataState((prev) => ({
        ...prev,
        markerPosition: markerPosition,
        address_components: extractAddress,
        full_address: place.formatted_address,
        ...result,
      }))

      onChange({
        markerPosition,
        address_components: extractAddress,
        full_address: place.formatted_address,
        ...result,
        url,
        disabled,
      })
    }

    const getLocationFromLatLng = async (lat, lng) => {
      try {
        let isInZone = false
        let zoneId = null
        let zoneName = ''
        let colony_name = null
        let colony_number = null

        const mapData = mapDataRef.current

        if (mapData.geoZonesCoords && mapData.geoZonesCoords.length > 0) {
          for (const item of mapData.geoZonesCoords) {
            const polygon = new window.google.maps.Polygon({
              paths: item.coords,
            })
            if (
              window.google.maps.geometry.poly.containsLocation(
                new window.google.maps.LatLng(lat, lng),
                polygon
              )
            ) {
              isInZone = true
              zoneId = item.zone?.id || null
              zoneName = item.zone?.name || ''
              break // Detener la iteración al encontrar la primera coincidencia
            }
          }
        }

        if (!isInZone)
          throw new Error(
            'La ubicación seleccionada no está dentro de una zona válida'
          )

        if (mapData.geoColoniesCoords && mapData.geoColoniesCoords.length > 0) {
          for await (const item of mapData.geoColoniesCoords) {
            const polygon = new window.google.maps.Polygon({
              paths: item.coords,
            })
            if (
              window.google.maps.geometry.poly.containsLocation(
                new window.google.maps.LatLng(lat, lng),
                polygon
              )
            ) {
              colony_number = item?.nocolonia || null //nocolonia
              colony_name = item?.name || null //name
              break // Detener la iteración al encontrar la primera coincidencia
            }
          }
        }

        const url = `https://www.google.com/maps?q=${lat},${lng}`

        return {
          url,
          zone_id: zoneId,
          zone_name: zoneName,
          colony_name,
          colony_number,
        }
      } catch (error) {
        return {
          error: true,
          message: error.message || 'Error al obtener la ubicación',
        }
      }
    }

    const handleAlertError = (message) => {
      setMapDataState((prev) => ({
        ...prev,
        zoneIndex: null,
        markerPosition: null,
        address_components: {},
        colony_name: '',
        colony_number: null,
        full_address: '',
        position: {},
        url: '',
        zone_id: null,
        zone_name: '',
        addressValue: '',
      }))

      onChange({
        zoneIndex: null,
        markerPosition: null,
        address_components: {},
        colony_name: '',
        colony_number: null,
        full_address: '',
        position: {},
        url: '',
        zone_id: null,
        zone_name: '',
        addressValue: '',
      })
      toastSweetAlert({ mode: 'error', message })
    }

    return (
      <>
        {loadError && (
          <div className="alert alert-default-warning">
            Error al cargar mapa
          </div>
        )}

        {errorSubs && (
          <div className="alert alert-default-warning">
            Error al cargar las subjurisdicciones
          </div>
        )}

        {!loadError &&
          !errorSubs &&
          (!isLoaded || loadingSubs || !dataSubs) && <Loader size="2rem" />}

        {!loadError && !errorSubs && isLoaded && !loadingSubs && dataSubs && (
          <>
            <GoogleMap
              mapContainerStyle={mapContainerStyle}
              center={mapDataState.markerPosition || defaultCenter}
              zoom={mapDataState.markerPosition ? 15 : 14}
              options={{
                ...optionsGoogleMap,
                styles: stylesGoogleMap,
              }}
              onLoad={subJurisdictionsReady ? onLoad : undefined}
              clickableIcons
            >
              {mapDataState.markerPosition && (
                <Marker position={mapDataState.markerPosition} />
              )}

              {mapDataState.geoZonesCoords.map((item, index) => (
                <GoogleMapZonePolygon
                  key={`${item.name}-${index}`}
                  item={item}
                  index={index}
                  onChange={!disabled ? handleClickMap : null}
                />
              ))}
            </GoogleMap>
            {showInputPlaces && (
              <GooglePlacesSearch
                disabled={disabled}
                addressFull={mapDataState.addressValue}
                onPlaceSelected={handleClickPlace}
                name="address_places"
              />
            )}
          </>
        )}
      </>
    )
  }
)

export default React.memo(GoogleMapComponentV3)
